import React, { useState } from 'react'
import {
  FileText, Clock, Send, CreditCard, Receipt, Lock, XCircle,
  Building, Car, Loader2, Euro, MessageSquare,
  AlertCircle, Calendar, MapPin, Shield, User,
} from 'lucide-react'
import { toast } from 'sonner'
import AvancementAmiableBlock from './AvancementAmiableBlock'
import ReglementAmiableBlock from './ReglementAmiableBlock'

const API_URL = process.env.REACT_APP_BACKEND_URL

// Pipeline amiable
const AMIABLE_STAGES = [
  { key: 'declare', label: 'Instruction', icon: FileText },
  { key: 'en_attente_elements', label: 'En attente', icon: Clock },
  { key: 'reglement_demande', label: 'Reglement demande', icon: Send },
  { key: 'reglement_effectue', label: 'Reglement effectue', icon: CreditCard },
  { key: 'attente_facture', label: 'Attente facture', icon: Receipt },
  { key: 'cloture_favorable', label: 'Cloture', icon: Lock },
]

// Nom + prénom conducteur en une seule ligne (sans doublon si le nom contient déjà le prénom)
const conducteurNomPrenom = (s) => {
  const nom = (s.chauffeur_nom_saisi || '').trim()
  const brut = (s.chauffeur_prenom_saisi || '').trim()
  const prenom = /[A-Za-zÀ-ÿ]/.test(brut) ? brut : ''
  if (nom && prenom && !nom.toLowerCase().includes(prenom.toLowerCase())) return `${nom} ${prenom}`
  return nom || prenom
}

const fmt = (v) => {
  if (v == null || v === '' || isNaN(v)) return ''
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v)
}

export default function AmiableSinistreView({ sinistre, onRefresh, contactsSlot = null }) {
  const [saving, setSaving] = useState(false)

  const statut = sinistre?.statut || 'declare'
  const isRefuse = statut === 'cloture_refus' || statut === 'refuse'
  const isClosed = statut?.startsWith('cloture_') || statut === 'sans_suite'

  const currentIdx = AMIABLE_STAGES.findIndex(s => s.key === statut)
  const pipelineIdx = currentIdx >= 0 ? currentIdx : (isClosed ? AMIABLE_STAGES.length - 1 : 0)

  async function handleStatutChange(newStatut) {
    setSaving(true)
    try {
      const r = await fetch(`${API_URL}/api/sinistres/${sinistre.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: newStatut }),
      })
      if (!r.ok) throw new Error('Erreur')
      toast.success(`Statut : ${AMIABLE_STAGES.find(s => s.key === newStatut)?.label || newStatut}`)
      onRefresh()
    } catch {
      toast.error('Erreur')
    } finally {
      setSaving(false)
    }
  }

  async function handleRefuse() {
    setSaving(true)
    try {
      await fetch(`${API_URL}/api/sinistres/${sinistre.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: 'cloture_refus' }),
      })
      toast.success('Dossier refuse')
      onRefresh()
    } catch {
      toast.error('Erreur')
    } finally {
      setSaving(false)
    }
  }

  // Missing fields for red highlight
  const missing = []
  if (!sinistre.montant_amiable_ht && sinistre.montant_amiable_ht !== 0) missing.push('Montant HT')
  if (!sinistre.nom_garage) missing.push('Nom du garage')
  if (!sinistre.societe_proprietaire) missing.push('Societe proprietaire')
  const missingSet = new Set(missing)

  return (
    <div className="space-y-4" data-testid="amiable-sinistre-view">
      {/* Alerte champs manquants */}
      {missing.length > 0 && !isClosed && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl" data-testid="amiable-missing-alert">
          <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">{missing.length} champ(s) a completer</p>
            <p className="text-xs text-red-600 mt-0.5">{missing.join(' · ')}</p>
          </div>
        </div>
      )}

      {/* ═══ ROW 1 : Infos sinistre COMPLETES (gauche) + Avancement (droite) ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Col gauche: Informations du sinistre — 3 sous-blocs */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="amiable-info-sinistre-block">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
              <FileText size={16} className="text-indigo-500" />
              Informations du sinistre
            </h2>
            {sinistre.est_amiable && <span className="ml-2 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-teal-100 text-teal-700 border border-teal-200">Amiable</span>}
          </div>
          <div className="p-5 space-y-5">
            {/* Sous-bloc 1 : Sinistre */}
            <div data-testid="amiable-sb-sinistre">
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-3">
                <InlineInput label="N dossier client" value={sinistre.numero_sinistre_client} field="numero_sinistre_client" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<FileText size={12} className="text-slate-400" />} />
                <InlineInput label="Date du sinistre" value={sinistre.date_fait} field="date_fait" sinistreId={sinistre.id} onRefresh={onRefresh} type="date" icon={<Calendar size={12} className="text-slate-400" />} />
                <InlineInput label="Lieu du sinistre" value={sinistre.lieu || sinistre.lieu_sinistre?.adresse} field="lieu" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<MapPin size={12} className="text-slate-400" />} />
                <InlineInput label="Constat" value={['true', 'false'].includes(String(sinistre.constat)) ? '' : sinistre.constat} field="constat" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<FileText size={12} className="text-slate-400" />} />
              </div>
              <div className="mt-3">
                <InlineInput label="Description" value={['Déclaration flotte', 'Pre-declaration amiable', 'Pre-declaration flotte'].includes(sinistre.description) ? '' : sinistre.description} field="description" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<FileText size={12} className="text-slate-400" />} />
              </div>
              <div className="mt-3 grid grid-cols-1 xl:grid-cols-4 gap-x-6 gap-y-3">
                <div className="xl:col-span-3">
                  <InlineInput label="Canal / identité / date décla" value={sinistre.declarant_identite} field="declarant_identite" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<User size={12} className="text-slate-400" />} />
                </div>
                <InlineInput label="Depot" value={sinistre.nom_chef_depot} field="nom_chef_depot" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<Building size={12} className="text-slate-400" />} />
              </div>
            </div>
            {/* Sous-bloc 2 : Information client */}
            <div className="pt-4 border-t-2 border-blue-600" data-testid="amiable-sb-client">
              <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wide mb-3">Information client</p>
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-3">
                <InlineInput label="Immatriculation" value={sinistre.vehicules?.immatriculation || sinistre.immatriculation_saisie} field="immatriculation_saisie" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<Car size={12} className="text-slate-400" />} />
                <InlineInput label="Modele" value={sinistre.modele_saisi || (sinistre.vehicules ? `${sinistre.vehicules.marque || ""} ${sinistre.vehicules.modele || ""}`.trim() : "")} field="modele_saisi" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<Car size={12} className="text-slate-400" />} />
                <InlineInput label="Nom Prénom conducteur" value={conducteurNomPrenom(sinistre)} field="chauffeur_nom_saisi" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<User size={12} className="text-slate-400" />} extra={{ chauffeur_prenom_saisi: null }} />
                <InlineInput label="Filiale" value={sinistre.societe_proprietaire} field="societe_proprietaire" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<Building size={12} className="text-slate-400" />} />
              </div>
            </div>
            {/* Sous-bloc 3 : Information tiers */}
            <div className="pt-4 border-t-2 border-blue-600" data-testid="amiable-sb-tiers">
              <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wide mb-3">Information tiers</p>
              <div className="grid grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
                <InlineInput label="Nom / Prénom / Société" value={sinistre.tiers_nom || sinistre.info_tiers} field="tiers_nom" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<User size={12} className="text-slate-400" />} />
                <InlineInput label="Telephone" value={sinistre.tiers_telephone} field="tiers_telephone" sinistreId={sinistre.id} onRefresh={onRefresh} />
                <InlineInput label="Adresse mail" value={sinistre.tiers_email} field="tiers_email" sinistreId={sinistre.id} onRefresh={onRefresh} />
              </div>
              <div className="mt-3 grid grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-3">
                <InlineInput label="Immatriculation tiers" value={sinistre.immatriculation_tiers} field="immatriculation_tiers" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<Car size={12} className="text-slate-400" />} />
                <InlineInput label="Modele tiers" value={sinistre.tiers_vehicule} field="tiers_vehicule" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<Car size={12} className="text-slate-400" />} />
                <InlineInput label="Dommages" value={sinistre.dommages_tiers} field="dommages_tiers" sinistreId={sinistre.id} onRefresh={onRefresh} />
              </div>
              <div className="mt-3">
                <InlineInput label="Garage" value={sinistre.nom_garage} field="nom_garage" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<Building size={12} className="text-slate-400" />} placeholder="Nom, coordonnées, infos du garage…" />
              </div>
            </div>
            {/* Sous-bloc 4 : Information règlement */}
            <div className="pt-4 border-t-2 border-blue-600" data-testid="amiable-sb-reglement">
              <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wide mb-3">Information règlement</p>
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-3">
                <InlineInput label="Montant HT" value={sinistre.montant_amiable_ht} field="montant_amiable_ht" sinistreId={sinistre.id} onRefresh={onRefresh} type="number" icon={<Euro size={12} className="text-slate-400" />} missing={missingSet.has('Montant HT')} />
                <InlineInput label="Montant TTC" value={sinistre.montant_amiable_ttc} field="montant_amiable_ttc" sinistreId={sinistre.id} onRefresh={onRefresh} type="number" icon={<Euro size={12} className="text-slate-400" />} />
              </div>
              <div className="mt-3">
                <InlineInput label="Commentaire" value={sinistre.commentaire_montant_amiable} field="commentaire_montant_amiable" sinistreId={sinistre.id} onRefresh={onRefresh} icon={<MessageSquare size={12} className="text-slate-400" />} multiline />
              </div>
            </div>
          </div>
        </div>

        {/* Col droite: Avancement */}
        <div className="space-y-4">
          <AvancementAmiableBlock sinistre={sinistre} onRefresh={onRefresh} />
        </div>
      </div>

      {/* ═══ ROW 2 : Contacts (slot fourni par la fiche) + Reglement amiable ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-4" data-testid="amiable-contacts-slot">
          {contactsSlot}
        </div>
        <div className="space-y-4">
          <ReglementAmiableBlock sinistre={sinistre} onRefresh={onRefresh} />
        </div>
      </div>
    </div>
  )
}

// ── Inline editable input with auto-save on blur ──
function InlineInput({ label, value, field, sinistreId, onRefresh, type = 'text', icon, missing, multiline = false, extra }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value || '')
  const [saving, setSaving] = useState(false)
  const originalRef = React.useRef(value || '')

  React.useEffect(() => {
    setVal(value || '')
    originalRef.current = value || ''
  }, [value])

  async function handleAutoSave() {
    const newVal = type === 'number' ? parseFloat(val) || null : val || null
    const origVal = type === 'number' ? parseFloat(originalRef.current) || null : originalRef.current || null
    if (newVal === origVal) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      const r = await fetch(`${API_URL}/api/sinistres/${sinistreId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...(extra || {}), [field]: newVal }),
      })
      if (!r.ok) throw new Error('Erreur')
      originalRef.current = val
      setEditing(false)
      onRefresh()
      // enregistrement groupe a la sortie du dossier (brouillon de fiche)
    } catch {
      toast.error('Erreur de sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <div>
        <label className="block text-[10px] font-medium text-slate-500 mb-0.5 flex items-center gap-1">{icon}{label}</label>
        <div className="relative">
          {multiline ? (
            <textarea
              value={val}
              onChange={e => setVal(e.target.value)}
              rows={4}
              className="w-full px-2.5 py-1.5 text-xs border border-indigo-300 rounded-lg focus:ring-1 focus:ring-indigo-400 pr-6 resize-y min-h-[80px]"
              autoFocus
              onBlur={handleAutoSave}
              onKeyDown={e => { if (e.key === 'Escape') { setVal(originalRef.current); setEditing(false) } }}
              data-testid={`inline-input-${field}`}
            />
          ) : (
          <input
            type={type}
            step={type === 'number' ? '0.01' : undefined}
            value={val}
            onChange={e => setVal(e.target.value)}
            className="w-full px-2.5 py-1.5 text-xs border border-indigo-300 rounded-lg focus:ring-1 focus:ring-indigo-400 pr-6"
            autoFocus
            onBlur={handleAutoSave}
            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setVal(originalRef.current); setEditing(false) } }}
            data-testid={`inline-input-${field}`}
          />
          )}
          {saving && <Loader2 size={10} className="animate-spin text-indigo-500 absolute right-2 top-1/2 -translate-y-1/2" />}
        </div>
      </div>
    )
  }

  const isEmpty = !value && value !== 0
  return (
    <div
      onClick={() => setEditing(true)}
      className={`cursor-pointer group ${missing ? 'ring-1 ring-red-200 rounded-lg p-1.5 -m-1.5 bg-red-50/30' : ''}`}
    >
      <label className="block text-[10px] font-medium text-slate-500 mb-0.5 flex items-center gap-1">
        {icon}
        {label}
      </label>
      {isEmpty ? (
        <p className="text-xs text-red-400 italic group-hover:text-indigo-500">Non renseigne — cliquer pour saisir</p>
      ) : (
        <p className={`text-sm text-slate-800 font-medium group-hover:text-indigo-600 ${multiline ? 'whitespace-pre-wrap break-words' : ''}`}>
          {type === 'number' ? fmt(value) : type === 'date' ? new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : value}
        </p>
      )}
    </div>
  )
}
